import React from "react";
import styled from "@emotion/styled";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import {
  Avatar,
  Box,
  Divider,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import whiteLogo from "../../assets/logos/light_logo.svg";
import blackLogo from "../../assets/logos/dark_logo.svg";
import whiteLogoIcon from "../../assets/logos/logo_icon.svg";
import blackLogoIcon from "../../assets/logos/logo_icon.svg";
import orgPortalIcon from "../../assets/images/logo.png"
import inkitIcon from "../../assets/logos/inkit.jpg";
import { routes } from "../../routes/RouteWrapper";
import LogoutIcon from "@mui/icons-material/LogoutOutlined";
import {
  logOut,
  selectCurrentUserObject,
  setUserType,
} from "../../store/auth/authSlice";
import profileimg from "../../assets/images/dashboard-image/default-user-profie.jpg";

const drawerWidth = 240;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.primary.drawerText,
  // boxShadow: 'rgba(0, 0, 0, 0.2) 0px 2px 10px 0px',
  overflowX: "hidden",
  minHeight: "100vh",

  [theme.breakpoints.up("xs")]: {
    position: "fixed",
    zIndex: "9999",
  },

  [theme.breakpoints.up("lg")]: {
    position: "fixed",
  },
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.primary.drawerText,
  // boxShadow: 'rgba(0, 0, 0, 0.2) 0px 2px 10px 0px',
  overflowX: "hidden",
  width: `calc(70px + 1px)`,
  [theme.breakpoints.up("xs")]: {
    left: "-200px",
    zIndex: "9999",
    position: "fixed",
  },
  [theme.breakpoints.up("lg")]: {
    left: "0px",
    zIndex: "9999",
    position: "fixed",
  },
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const DrawerHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const Sidebar = ({ open }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userType = useSelector((state) => state.auth.userType);
  const location = useLocation();
  let user = useSelector(selectCurrentUserObject);
  const { social_profile_pic = "", first_name = "" } = user;
  console.log("🚀 ~ Sidebar ~ user:", user)

  const sidebarLogo = orgPortalIcon;

  const handleSignOut = async () => {
    dispatch(logOut());
    dispatch(setUserType(null));
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    navigate("/login", { state: { from: location }, replace: true });
  };

  return (
    <Drawer variant="permanent" open={open} sx={{ left: "0" }}>
      <DrawerHeader>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            gap: "10px",
            marginTop: "15px",
          }}
        >
          <img src={sidebarLogo} alt="BrangChain-logo" height={60} />
        </Box>
      </DrawerHeader>
      <List
        sx={{
          flexGrow: 1,
        }}
      >
        {routes?.map((route, index) => {
          const { permissions, isMenu, icon } = route;
          const shouldDisplay =
            isMenu &&
            (permissions.includes(userType) ||
              (permissions?.length === 0 && user.is_fredit));
          if (!shouldDisplay) return null;
          return (
            <ListItem
              key={index}
              disablePadding
              sx={{
                display: "block",
                "& a": {
                  textDecoration: "none",
                },
              }}
            >
              <Link to={route.path} target={route.target}>
                <ListItemButton
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? "initial" : "center",
                    px: 2.5,
                    color: location.pathname.includes(route.path)
                      ? theme.palette.common.white
                      : theme?.palette?.mode === "light"
                      ? theme.palette.grey[800]
                      : theme.palette.grey[500],
                    backgroundColor: location.pathname.includes(route.path)
                      ? theme.palette.primary.main
                      : theme.palette.background.paper,
                    borderRadius: "4px",
                    margin: "10px",
                    "&:hover": {
                      backgroundColor: theme.palette.primary?.main,
                      color: "#fff",
                      "& svg": {
                        color: theme.palette.common.white,
                      },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : "auto",
                      justifyContent: "center",
                      color: location.pathname.includes(route.path)
                        ? theme.palette.common.white
                        : theme?.palette?.mode === "light"
                        ? theme.palette.grey[800]
                        : theme.palette.grey[600],
                    }}
                  >
                    {icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={route.name}
                    sx={{
                      height: open ? "100%" : "0px",
                      opacity: open ? 1 : 0,
                      whiteSpace: route.name?.length >= 20 ? "normal" : "unset",
                    }}
                  />
                </ListItemButton>
              </Link>
            </ListItem>
          );
        })}
      </List>

      {/* Profile Section */}
      <Box sx={{ mt: "auto" }}>
        <Divider />

        <List>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => navigate("/profile")}
              sx={{
                minHeight: 48,
                justifyContent: open ? "initial" : "center",
                px: 2.5,
                mx: 2,
                borderRadius: 1,
                "&:hover": {
                  bgcolor: "error.lighter",
                  "& svg": { color: "error.main" },
                },
              }}
            >
              <ListItemIcon
                id="basic-button"
                sx={{
                  padding: "0px",
                  cursor: "pointer",
                  width: "40px",
                  height: "40px",
                  flex: " 0 0 40px",
                  minWidth: "inherit",
                  marginLeft: open ? "-5px" : "0px",
                  "& img": {
                    width: "100%",
                    height: "100%",
                    borderRadius: "100%",
                    objectFit: "cover",
                  },
                }}
              >
                <img src={social_profile_pic ? social_profile_pic : profileimg} alt="profile-img" />
              </ListItemIcon>
              <ListItemText
                primary={user?.email}
                sx={{
                  opacity: open ? 1 : 0,
                  marginLeft: open ? "15px" : "0",
                }}
              />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton
              onClick={handleSignOut}
              sx={{
                minHeight: 48,
                justifyContent: open ? "initial" : "center",
                px: 2.5,
                mx: 2,
                borderRadius: 1,
                color: "error.main",
                "&:hover": {
                  bgcolor: "error.lighter",
                  "& svg": { color: "error.main" },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : "auto",
                  justifyContent: "center",
                  color: "error.main",
                }}
              >
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText
                primary="Logout"
                sx={{
                  opacity: open ? 1 : 0,
                  color: "inherit",
                }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
export { DrawerHeader };
